import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProjectPivotTableDevExtreme from "../components/ProjectPivotTableDevExtreme";
import { FormControl, InputLabel, Select, MenuItem, Typography, Box, Autocomplete, TextField } from '@mui/material';
import { fetchAllProjects } from "../api";
import FullWidthLayout from "../components/FullWidthLayout";
import { useTranslation } from "react-i18next";
import { formatLocalDate } from "../utils/localDateTime";

const formatProjectDisplayName = (project) => {
  const name = project?.project_name || '';
  if (project?.project_id == null) return name;
  return name ? `${name} (${project.project_id})` : `(${project.project_id})`;
};

const getProjectDueYear = (project) => {
  const dueDate = formatLocalDate(project?.due_date);
  return dueDate ? dueDate.slice(0, 4) : null;
};

const ProjectPivotPage = () => {
  const { project_id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  // Set BOM view as default: 'cost' for BOM, 'hierarchical' for collapsed tree
  const [viewType, setViewType] = useState('cost');
  const [layoutType, setLayoutType] = useState('hierarchical');
  const [selectedProjectValue, setSelectedProjectValue] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  // controlled input value for the Autocomplete so it stays in sync after selection
  const [projectInputValue, setProjectInputValue] = useState('');

  useEffect(() => {
    fetchAllProjects().then((projects) => {
      // sort alphabetically by project_name (case-insensitive)
      const sorted = projects.slice().sort((a, b) => {
        const A = (a.project_name || '').toLowerCase();
        const B = (b.project_name || '').toLowerCase();
        if (A < B) return -1;
        if (A > B) return 1;
        return 0;
      });

      setProjects(sorted);

      if (project_id) {
        const currentProject = sorted.find(p => p.project_id === parseInt(project_id));
        setSelectedProjectValue(currentProject || null);
        setProjectInputValue(formatProjectDisplayName(currentProject));
      } else if (sorted.length > 0) {
        navigate(`/pivot/project/${sorted[0].project_id}`);
      }
    }).catch(console.error);
  }, [project_id, navigate]);

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleProjectChange = (event, newValue) => {
    if (newValue) {
      // keep selected value and input in sync
      setSelectedProjectValue(newValue);
      setProjectInputValue(formatProjectDisplayName(newValue));
      navigate(`/pivot/project/${newValue.project_id}`);
    } else {
      setSelectedProjectValue(null);
      setProjectInputValue('');
    }
  };

  const handleViewTypeChange = (event) => {
    setViewType(event.target.value);
  };

  const handleLayoutTypeChange = (event) => {
    setLayoutType(event.target.value);
  };

  const selectedProject = project_id
    ? projects.find(p => p.project_id === parseInt(project_id))
    : null;

  const projectName = selectedProject?.project_name || '';
  const projectDisplayName = formatProjectDisplayName(selectedProject);
  const formattedDueDate = (() => {
    if (!selectedProject?.due_date) return null;
    return formatLocalDate(selectedProject.due_date) || null;
  })();

  // Get unique years from project due dates
  const years = [...new Set(projects
    .map(getProjectDueYear)
    .filter(Boolean)
    .sort((a, b) => b - a)
    .slice(0, 10))];

  // We'll let Autocomplete call our custom filterOptions to enforce matching.
  const projectOptions = selectedYear
    ? projects.filter(p => getProjectDueYear(p) === selectedYear)
    : projects;

  const normalizeProjectSearchInput = (inputValue) => {
    const input = (inputValue || '').toLowerCase().trim();
    const selectedDisplayName = formatProjectDisplayName(selectedProjectValue).toLowerCase();

    if (input && input === selectedDisplayName) {
      return (selectedProjectValue?.project_name || '').toLowerCase().trim();
    }

    return input;
  };

  return (
    <FullWidthLayout>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl sx={{ flex: 1 }} variant="outlined" margin="normal">
          <InputLabel id="year-select-label">{t("projectPivot.filters.year.label")}</InputLabel>
          <Select
            labelId="year-select-label"
            value={selectedYear}
            onChange={handleYearChange}
            label={t("projectPivot.filters.year.label")}
          >
            <MenuItem value="">{t("projectPivot.filters.year.all")}</MenuItem>
            {years.map(year => (
              <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          sx={{ flex: 2 }}
          fullWidth
          value={selectedProjectValue}
          onChange={handleProjectChange}
          // control the typed input so it stays in sync after selection
          inputValue={projectInputValue}
          onInputChange={(event, newInputValue) => setProjectInputValue(newInputValue)}
          options={projectOptions}
          // enforce case-insensitive substring matching (anywhere in the name)
          filterOptions={(options, state) => {
            const input = normalizeProjectSearchInput(state.inputValue);
            if (!input) return options;
            return options.filter(p => {
              const name = (p.project_name || '').toLowerCase();
              const id = p.project_id?.toString() || '';
              const displayName = formatProjectDisplayName(p).toLowerCase();
              return name.includes(input) || id.includes(input) || displayName.includes(input);
            });
          }}
          getOptionLabel={formatProjectDisplayName}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("projectPivot.filters.projectSearch.label")}
              variant="outlined"
              margin="normal"
              placeholder={t("projectPivot.filters.projectSearch.placeholder")}
            />
          )}
          isOptionEqualToValue={(option, value) => option?.project_id === value?.project_id}
        />
        <FormControl sx={{ flex: 1 }} variant="outlined" margin="normal">
          <InputLabel id="view-type-select-label">{t("projectPivot.filters.viewType.label")}</InputLabel>
          <Select
            labelId="view-type-select-label"
            value={viewType}
            onChange={handleViewTypeChange}
            label={t("projectPivot.filters.viewType.label")}
          >
            <MenuItem value="cost">{t("projectPivot.filters.viewType.cost")}</MenuItem>
            <MenuItem value="time">{t("projectPivot.filters.viewType.time")}</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ flex: 1 }} variant="outlined" margin="normal">
          <InputLabel id="layout-type-select-label">{t("projectPivot.filters.layout.label")}</InputLabel>
          <Select
            labelId="layout-type-select-label"
            value={layoutType}
            onChange={handleLayoutTypeChange}
            label={t("projectPivot.filters.layout.label")}
          >
            <MenuItem value="crosstab">{t("projectPivot.filters.layout.crosstab")}</MenuItem>
            <MenuItem value="hierarchical">{t("projectPivot.filters.layout.hierarchical")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {project_id && (
        <>
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'baseline' }}>
              {t("projectPivot.header.title", { name: projectDisplayName || projectName })}
              {formattedDueDate && (
                <Box component="span" sx={{ ml: 1, fontSize: '1.25rem', color: 'text.secondary', fontWeight: 400 }}>
                  {t("projectPivot.header.due", { date: formattedDueDate })}
                </Box>
              )}
            </Typography>
          </Box>
          <ProjectPivotTableDevExtreme
            projectId={project_id}
            viewType={viewType}
            layoutType={layoutType}
          />
        </>
      )}
    </FullWidthLayout>
  );
};

export default ProjectPivotPage;
